import React from "react";
import "./HTML_MAIN_CSS.css";

const HTML_Audio = () => {
  return (
    <div className="html-container">
      <h1>HTML Audio - Deep Notes & Explanation</h1>

      {/* 1. Introduction */}
      <section className="html-section">
        <h2>1. Introduction to HTML Audio</h2>
        <p>
          The <code>&lt;audio&gt;</code> element in HTML is used to embed sound
          content, such as music, narration, or podcasts, into web pages.
          Previously, playing audio required plugins like Flash — now HTML5
          provides a built-in, simple solution.
        </p>
        <div className="problem">
          <strong>Problem Statement:</strong>  
          Before HTML5, developers had to rely on third-party plugins to play
          sound, which led to compatibility and security issues. The HTML
          <code>&lt;audio&gt;</code> tag solves this by natively supporting audio
          playback across modern browsers.
        </div>
      </section>

      {/* 2. Basic Syntax */}
      <section className="html-section">
        <h2>2. Basic Syntax</h2>
        <pre className="code-block">
          <code>{`<audio controls>
  <source src="audio-file.mp3" type="audio/mpeg">
  Your browser does not support the audio element.
</audio>`}</code>
        </pre>

        <p>
          The <code>&lt;audio&gt;</code> tag requires at least one{" "}
          <code>&lt;source&gt;</code> element that specifies the audio file and
          its MIME type.
        </p>
        <audio controls>
          <source
            src="https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3"
            type="audio/mpeg"
          />
          Your browser does not support the audio element.
        </audio>
      </section>

      {/* 3. Attributes */}
      <section className="html-section">
        <h2>3. Important Attributes of &lt;audio&gt;</h2>
        <ul>
          <li>
            <strong>controls:</strong> Displays default play, pause, and volume
            controls.
          </li>
          <li>
            <strong>autoplay:</strong> Automatically starts playing the audio
            when the page loads.
          </li>
          <li>
            <strong>loop:</strong> Repeats the audio once it ends.
          </li>
          <li>
            <strong>muted:</strong> Mutes the audio by default.
          </li>
          <li>
            <strong>preload:</strong> Determines how audio is loaded:
            <ul>
              <li>
                <code>none</code> – Audio not loaded until play is clicked.
              </li>
              <li>
                <code>metadata</code> – Only metadata (duration, etc.) loaded.
              </li>
              <li>
                <code>auto</code> – Audio file preloaded fully.
              </li>
            </ul>
          </li>
        </ul>

        <pre className="code-block">
          <code>{`<audio controls autoplay loop muted preload="auto">
  <source src="music.mp3" type="audio/mpeg">
</audio>`}</code>
        </pre>
      </section>

      {/* 4. Multiple Source Support */}
      <section className="html-section">
        <h2>4. Multiple Audio Sources</h2>
        <p>
          You can provide multiple audio formats to ensure browser
          compatibility. The browser plays the first supported format.
        </p>
        <pre className="code-block">
          <code>{`<audio controls>
  <source src="sound.mp3" type="audio/mpeg">
  <source src="sound.ogg" type="audio/ogg">
  <source src="sound.wav" type="audio/wav">
  Your browser does not support the audio element.
</audio>`}</code>
        </pre>
      </section>

      {/* 5. Common Audio Formats */}
      <section className="html-section">
        <h2>5. Common Audio Formats</h2>
        <table className="styled-table">
          <thead>
            <tr>
              <th>Format</th>
              <th>File Extension</th>
              <th>MIME Type</th>
              <th>Browser Support</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>MP3</td>
              <td>.mp3</td>
              <td>audio/mpeg</td>
              <td>All major browsers</td>
            </tr>
            <tr>
              <td>Ogg</td>
              <td>.ogg</td>
              <td>audio/ogg</td>
              <td>Firefox, Chrome, Opera</td>
            </tr>
            <tr>
              <td>WAV</td>
              <td>.wav</td>
              <td>audio/wav</td>
              <td>All major browsers</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* 6. Accessibility */}
      <section className="html-section">
        <h2>6. Accessibility Considerations</h2>
        <ul>
          <li>Provide transcripts for audio content for hearing-impaired users.</li>
          <li>Ensure autoplay is disabled by default to prevent disruption.</li>
          <li>Use descriptive titles or <code>aria-label</code> attributes.</li>
        </ul>
      </section>

      {/* 7. Audio Events */}
      <section className="html-section">
        <h2>7. Common Audio Events</h2>
        <p>
          You can control and track audio playback using JavaScript event
          listeners.
        </p>
        <pre className="code-block">
          <code>{`<audio id="player" controls>
  <source src="song.mp3" type="audio/mpeg">
</audio>

<script>
  const player = document.getElementById("player");
  player.onplay = () => console.log("Audio started playing");
  player.onpause = () => console.log("Audio paused");
  player.onended = () => console.log("Audio ended");
</script>`}</code>
        </pre>
      </section>

      {/* 8. Best Practices */}
      <section className="html-section">
        <h2>8. Best Practices for HTML Audio</h2>
        <ul>
          <li>Always provide user controls unless autoplay is necessary.</li>
          <li>Optimize audio file size for faster loading.</li>
          <li>Offer multiple audio formats for better compatibility.</li>
          <li>Provide fallback text for unsupported browsers.</li>
        </ul>
      </section>

      {/* 9. Summary */}
      <section className="html-section">
        <h2>Summary</h2>
        <ul>
          <li>
            <code>&lt;audio&gt;</code> is used to embed sound content.
          </li>
          <li>Supports attributes like controls, autoplay, loop, and muted.</li>
          <li>Multiple source formats ensure browser compatibility.</li>
          <li>Accessibility and performance should always be considered.</li>
        </ul>
      </section>
    </div>
  );
};

export default HTML_Audio;
